import { useMutation } from '@tanstack/react-query';
import { ChevronRight, Plus, X } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import React, { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import {
  type SsoConnection,
  type SsoDomain,
  type UpdateSsoConnectionRequest,
  ssoCreateConnectionMutation,
  ssoDeleteConnectionMutation,
  ssoDeleteDomainMutation,
  ssoUpdateConnectionMutation,
} from '@column/api';
import { Button, Dialog, DialogClose, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@column/ui';

import { useFormErrorHandler } from '~/hooks/useFormErrorHandler';
import { getErrorMessage } from '~/lib/api/getErrorMessage';
import { AnimatedHeight } from '~/lib/approvals/components/ReviewDialog';
import { VerticalScrollFade } from '~/lib/components/VerticalScrollFade';
import { useNotificationStore } from '~/stores/Notification';
import { useStepUpStore } from '~/stores/StepUp';

import { ConnectionFormSections, type SSOConnectionFields } from './ConnectionFormSections';
import { DeleteConfirmDialog } from './DeleteConfirmDialog';
import { useDomainPanel } from './useDomainPanel';
import { isStepUpError } from './utils';

interface ConnectionDialogProps {
  connection?: SsoConnection;
  onClose: () => void;
  onSaved: () => Promise<unknown>;
  onCreated: (connectionId: string) => void;
}

export const ConnectionDialog: React.FC<ConnectionDialogProps> = ({ connection, onClose, onSaved, onCreated }) => {
  const { addSuccessNotification, addDangerNotification } = useNotificationStore();
  const { handleSubmitErrors } = useFormErrorHandler();
  const createConnection = useMutation(ssoCreateConnectionMutation());
  const updateConnection = useMutation(ssoUpdateConnectionMutation());
  const formMethods = useForm<SSOConnectionFields>({ defaultValues: { ...connection, client_secret: '' } });
  const {
    handleSubmit,
    reset,
    formState: { dirtyFields, isDirty, isSubmitting },
  } = formMethods;
  const [domainView, setDomainView] = useState<{ domainId?: string }>();
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [removingDomain, setRemovingDomain] = useState<SsoDomain>();
  const deleteConnection = useMutation(ssoDeleteConnectionMutation());
  const deleteDomain = useMutation(ssoDeleteDomainMutation());

  const handleRemoveDomain = async () => {
    if (!connection || !removingDomain) return;
    try {
      await deleteDomain.mutateAsync({
        path: { sso_connection_id: connection.id, sso_domain_id: removingDomain.id },
      });
      await onSaved();
      addSuccessNotification({ content: `${removingDomain.domain} removed` });
      if (domainView?.domainId === removingDomain.id) setDomainView(undefined);
    } catch (error) {
      if (!isStepUpError(error)) {
        addDangerNotification({ content: getErrorMessage(error) });
      }
    }
  };

  const handleDelete = async () => {
    if (!connection) return;
    try {
      await deleteConnection.mutateAsync({ path: { sso_connection_id: connection.id } });
      await onSaved();
      addSuccessNotification({ content: 'SSO connection deleted' });
      onClose();
    } catch (error) {
      if (!isStepUpError(error)) {
        addDangerNotification({ content: getErrorMessage(error) });
      }
    }
  };

  const handleSave = async (data: SSOConnectionFields) => {
    try {
      if (!connection) {
        const { display_name, issuer, client_id, client_secret } = data;
        const created = await createConnection.mutateAsync({
          body: { display_name, issuer, client_id, client_secret },
        });
        await onSaved();
        reset({ ...created, client_secret: '' });
        onCreated(created.id);
        addSuccessNotification({ content: 'SSO connection added' });
        return;
      } else {
        const body: UpdateSsoConnectionRequest = Object.fromEntries(
          Object.keys(dirtyFields).map((key) => [key, data[key as keyof SSOConnectionFields]])
        );
        await updateConnection.mutateAsync({ path: { sso_connection_id: connection.id }, body });
        addSuccessNotification({ content: 'SSO connection saved' });
      }
      await onSaved();
      onClose();
    } catch (error) {
      if (!isStepUpError(error)) {
        addDangerNotification({ content: getErrorMessage(error) });
      }
    }
  };

  const onSubmit = handleSubmit(handleSave, handleSubmitErrors);

  const domainPanel = useDomainPanel({
    connection,
    domainId: domainView?.domainId,
    isActive: !!domainView,
    onBack: () => setDomainView(undefined),
    onChanged: onSaved,
    onCreated: (domainId) => setDomainView({ domainId }),
    onRemove: setRemovingDomain,
    isRemoving: deleteDomain.isPending,
  });
  const isDomainView = !!connection && !!domainView;
  const isStepUpOpen = useStepUpStore((state) => state.challenge !== null);
  const isBusy = isSubmitting || domainPanel.isPending || deleteConnection.isPending || deleteDomain.isPending;

  return (
    <Dialog open onOpenChange={(open) => !open && !isStepUpOpen && !isBusy && onClose()}>
      <DialogContent
        showCloseButton={false}
        className="flex max-h-[calc(100dvh-4rem)] flex-col gap-0 p-0 sm:max-w-3xl"
        onInteractOutside={(event) => isStepUpOpen && event.preventDefault()}
        onEscapeKeyDown={(event) => isStepUpOpen && event.preventDefault()}
      >
        <DialogHeader className="shrink-0 flex-row items-center justify-between gap-4 border-b border-gray-100 px-6 pt-4.5 pb-4 dark:border-white/[0.08]">
          <DialogTitle className="flex min-w-0 items-center gap-1.5">
            {isDomainView ? (
              <button
                type="button"
                onClick={() => setDomainView(undefined)}
                className="cursor-pointer truncate text-muted-foreground transition-colors hover:text-foreground"
              >
                {connection.display_name}
              </button>
            ) : (
              <span className="truncate">{connection?.display_name ?? 'Add connection'}</span>
            )}
            <AnimatePresence initial={false}>
              {isDomainView && (
                <motion.span
                  key="domain-crumb"
                  className="flex min-w-0 items-center gap-1.5"
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -8 }}
                  transition={{ duration: 0.12, ease: 'easeOut' }}
                >
                  <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
                  <span className="truncate">{domainPanel.domain?.domain ?? 'Add Domain'}</span>
                </motion.span>
              )}
            </AnimatePresence>
          </DialogTitle>
          <DialogClose asChild>
            <button
              type="button"
              aria-label="Close"
              disabled={isBusy}
              className="-mr-1 flex size-6 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-gray-100 hover:text-foreground disabled:pointer-events-none disabled:opacity-50 dark:hover:bg-white/10"
            >
              <X className="size-4" />
            </button>
          </DialogClose>
        </DialogHeader>

        <VerticalScrollFade
          hintsOnEdges
          className="flex min-h-0 flex-1 flex-col"
          scrollClassName="min-h-0 flex-1 overflow-x-hidden"
        >
          <AnimatedHeight durationMs={140}>
            <AnimatePresence mode="wait" initial={false}>
              {isDomainView ? (
                <motion.div
                  key="domain"
                  initial={{ x: 48, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  exit={{ x: 48, opacity: 0 }}
                  transition={{ duration: 0.12, ease: 'easeOut' }}
                >
                  {domainPanel.body}
                </motion.div>
              ) : (
                <motion.div
                  key={connection ? `connection-${connection.id}` : 'connection-new'}
                  initial={{ x: -48, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  exit={{ x: -48, opacity: 0 }}
                  transition={{ duration: 0.12, ease: 'easeOut' }}
                >
                  <FormProvider {...formMethods}>
                    <form
                      autoComplete="off"
                      onSubmit={(event) => {
                        event.stopPropagation();
                        void onSubmit(event);
                      }}
                      className="[--page-margin:24px] [&_h2]:text-sm"
                    >
                      <ConnectionFormSections
                        connection={connection}
                        onAddDomain={() => setDomainView({})}
                        onSelectDomain={(domainId) => setDomainView({ domainId })}
                        onRemoveDomain={setRemovingDomain}
                      />
                    </form>
                  </FormProvider>
                </motion.div>
              )}
            </AnimatePresence>
          </AnimatedHeight>
        </VerticalScrollFade>

        <DialogFooter className="shrink-0 border-t border-gray-100 px-6 py-4 sm:justify-between dark:border-white/[0.08]">
          {isDomainView ? (
            domainPanel.footer
          ) : (
            <>
              <Button variant="secondary" size="sm" type="button" onClick={onClose} disabled={isBusy}>
                Cancel
              </Button>
              <div className="flex gap-2">
                {connection && (
                  <Button
                    variant="secondary-danger"
                    size="sm"
                    type="button"
                    onClick={() => setIsDeleteOpen(true)}
                    disabled={isSubmitting || deleteConnection.isPending}
                  >
                    Delete Connection
                  </Button>
                )}
                <Button
                  size="sm"
                  type="button"
                  onClick={onSubmit}
                  disabled={isSubmitting || (!!connection && !isDirty)}
                >
                  {!connection && <Plus />}
                  {connection ? 'Save Connection' : 'Add Connection'}
                </Button>
              </div>
            </>
          )}
        </DialogFooter>
        <DeleteConfirmDialog
          open={!!removingDomain}
          onOpenChange={(open) => !open && setRemovingDomain(undefined)}
          title="Remove domain?"
          description={`Users with ${removingDomain?.domain} email addresses will no longer sign in with ${connection?.display_name}.`}
          actionLabel="Remove"
          onConfirm={() => void handleRemoveDomain()}
        />
        <DeleteConfirmDialog
          open={isDeleteOpen}
          onOpenChange={setIsDeleteOpen}
          title="Delete connection?"
          description={`Your team will no longer be able to sign in with ${connection?.display_name}, and its domains will be removed. Existing SSO sessions stay valid until they expire.`}
          actionLabel="Delete"
          onConfirm={() => void handleDelete()}
        />
      </DialogContent>
    </Dialog>
  );
};
